import { Tool, ToolType } from './tool';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { IClonable } from '../elements/interfaces/clonable';
import { ToolGroup } from './tool-group';
import { PointElement } from '../elements/point-element';
import { MouseEventResult } from '../managers/manager-base';

export class CloneTool extends Tool {
    private sourceElement: IClonable | null = null;
    private isDragging: boolean = false;
    private mousePosition: { x: number, y: number } = { x: 0, y: 0 };
    private previewElements: IClonable[] = [];
    private hoveredElement: IClonable | null = null;
    private readonly HOVER_DISTANCE_THRESHOLD = 5; // pixels

    constructor(canvas: HTMLCanvasElement, levelDesigner: LevelDesignerComponent, toolGroup: ToolGroup) {
        super(canvas, levelDesigner, toolGroup, ToolType.Action, 'assets/icons/clone.svg');
    }

    public override getName(): string {
        return 'Clone';
    }

    public override getTooltip(): string {
        return 'Clone Selected Elements';
    }

    public override activate(): void {
        this.enabled = true;
        this.levelDesigner.redraw();
    }

    public override deactivate(): void {
        this.enabled = false;
        this.isDragging = false;
        this.sourceElement = null;
        this.previewElements = [];
        this.hoveredElement = null;
        this.levelDesigner.redraw();
    }

    public override onKeyPress(key: string): void {
        if (key === 'Escape' && this.isDragging) {
            this.cancel();
        }
    }

    public cancel(): void {
        this.isDragging = false;
        this.sourceElement = null;
        this.previewElements = [];
        this.levelDesigner.redraw();
    }

    private findClonableElementAtPosition(x: number, y: number): IClonable | null {
        for (const element of this.levelDesigner.elements) {
            if ('duplicate' in element && element.isPointInside(x, y)) {
                return element as unknown as IClonable;
            }
        }
        return null;
    }

    private calculateClonePositions(sourceElement: IClonable, endX: number, endY: number): { x: number, y: number }[] {
        const boundingBox = sourceElement.getBoundingBox();
        const startX = boundingBox.x + boundingBox.width / 2;
        const startY = boundingBox.y + boundingBox.height / 2;

        // Calculate vector from start to end
        const vectorX = endX - startX;
        const vectorY = endY - startY;
        const distance = Math.sqrt(vectorX * vectorX + vectorY * vectorY);

        // Calculate spacing based on element type and size
        const isPointElement = sourceElement instanceof PointElement;
        let spacing = sourceElement.cloneDistance;
        
        if (!isPointElement) {
            // For polygon elements, add the width in the direction of cloning
            const angle = Math.atan2(vectorY, vectorX);
            const projectedWidth = 
                Math.abs(Math.cos(angle) * boundingBox.width) + 
                Math.abs(Math.sin(angle) * boundingBox.height);
            spacing += projectedWidth;
        }

        if (spacing <= 0) return [];

        // Calculate number of clones that fit in the distance
        const numClones = Math.floor(distance / spacing);
        const positions: { x: number, y: number }[] = [];

        // Calculate normalized direction vector
        const dirX = vectorX / distance;
        const dirY = vectorY / distance;

        // Generate positions for each clone
        for (let i = 1; i <= numClones; i++) {
            positions.push({
                x: startX + dirX * spacing * i,
                y: startY + dirY * spacing * i
            });
        }

        return positions;
    }

    private snapTo45Degrees(startX: number, startY: number, endX: number, endY: number): { x: number, y: number } {
        const dx = endX - startX;
        const dy = endY - startY;
        const angle = Math.atan2(dy, dx);
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Snap to nearest 45 degrees (Math.PI / 4)
        const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);

        return {
            x: startX + Math.cos(snappedAngle) * distance,
            y: startY + Math.sin(snappedAngle) * distance
        };
    }

    private updatePreviewElements(mouseX: number, mouseY: number): void {
        if (!this.sourceElement) return;

        const bbox = this.sourceElement.getBoundingBox();
        const startX = bbox.x + bbox.width / 2;
        const startY = bbox.y + bbox.height / 2;

        let endPos = { x: mouseX, y: mouseY };
        if (this.levelDesigner.isShiftPressed) {
            endPos = this.snapTo45Degrees(startX, startY, mouseX, mouseY);
        }

        this.previewElements = [];
        const positions = this.calculateClonePositions(this.sourceElement, endPos.x, endPos.y);
        let previousClone: IClonable | undefined = undefined;

        for (const pos of positions) {
            const clone = this.sourceElement.duplicate(previousClone);
            clone.translate(
                pos.x - (this.sourceElement.getBoundingBox().x + this.sourceElement.getBoundingBox().width/2),
                pos.y - (this.sourceElement.getBoundingBox().y + this.sourceElement.getBoundingBox().height/2)
            );
            this.previewElements.push(clone);
            previousClone = clone;
        }

        this.levelDesigner.redraw();
    }

    public override onMouseMove(x: number, y: number): MouseEventResult | null {
        if (!this.isActive) return null;

        const worldCoords = this.levelDesigner.getWorldCoordinates({ 
            clientX: x + this.canvas.getBoundingClientRect().left, 
            clientY: y + this.canvas.getBoundingClientRect().top
        } as MouseEvent);

        this.mousePosition = worldCoords;

        if (this.isDragging && this.sourceElement) {
            this.updatePreviewElements(worldCoords.x, worldCoords.y);
            return {
                cursor: 'crosshair',
                stopPropagation: true,
                redraw: true
            };
        } else {
            // Check for clonable element under cursor
            const element = this.findClonableElementAtPosition(worldCoords.x, worldCoords.y);
            const hoverChanged = element !== this.hoveredElement;
            this.hoveredElement = element;

            return {
                cursor: 'url(/assets/icons/clone.svg) 16 16, crosshair',
                stopPropagation: true,
                redraw: hoverChanged
            };
        }
    }

    public override render(ctx: CanvasRenderingContext2D): void {
        if (!this.enabled) return;

        // Draw highlight for hovered element
        if (this.hoveredElement && !this.isDragging) {
            const bbox = this.hoveredElement.getBoundingBox();
            ctx.save();
            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 0.4;
            ctx.setLineDash([0.4, 0.4]);
            ctx.strokeRect(bbox.x, bbox.y, bbox.width, bbox.height);
            ctx.restore();
        }

        // Draw preview elements with semi-transparency
        ctx.globalAlpha = 0.5;
        for (const element of this.previewElements) {
            element.draw(ctx);
        }
        ctx.globalAlpha = 1.0;

        // Draw guide line from source to mouse position if dragging
        if (this.isDragging && this.sourceElement) {
            const bbox = this.sourceElement.getBoundingBox();
            const startX = bbox.x + bbox.width/2;
            const startY = bbox.y + bbox.height/2;

            let endPos = { x: this.mousePosition.x, y: this.mousePosition.y };
            if (this.levelDesigner.isShiftPressed) {
                endPos = this.snapTo45Degrees(startX, startY, this.mousePosition.x, this.mousePosition.y);
            }

            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endPos.x, endPos.y);
            ctx.strokeStyle = '#000000';
            ctx.setLineDash([0.1, 0.1]);
            ctx.stroke();
            ctx.setLineDash([]);
        }
    }

    public override onMouseDown(x: number, y: number): MouseEventResult | null {
        if (!this.isActive) return null;

        const worldCoords = this.levelDesigner.getWorldCoordinates({ 
            clientX: x + this.canvas.getBoundingClientRect().left, 
            clientY: y + this.canvas.getBoundingClientRect().top
        } as MouseEvent);

        const element = this.findClonableElementAtPosition(worldCoords.x, worldCoords.y);
        if (element) {
            this.sourceElement = element;
            this.isDragging = true;
            this.hoveredElement = null; // Clear hover highlight when starting drag
            return {
                cursor: 'crosshair',
                stopPropagation: true,
                redraw: false
            };
        }
        else {
            return {
                cursor: 'url(/assets/icons/clone.svg) 16 16, crosshair',
                stopPropagation: true,
                redraw: false
            };
        }

        return null;
    }

    public override onMouseUp(x: number, y: number): MouseEventResult | null {
        if (!this.isDragging || !this.sourceElement) return null;

        // Create all preview elements as real elements
        for (const preview of this.previewElements) {
            if ('checkPositionValid' in preview && 
                (preview as any).checkPositionValid(this.levelDesigner.elements).errors.length === 0) {
                this.levelDesigner.addElement(preview as any);
            }
        }

        this.isDragging = false;
        this.sourceElement = null;
        this.previewElements = [];
        this.levelDesigner.saveState();
        this.levelDesigner.redraw();
        
        return {
            cursor: 'default',
            stopPropagation: true,
            redraw: true
        };
    }
}
